import React, { useState, useRef, useEffect, memo } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "./Logo";
import SearchInput from "../SearchInput"; 
import UserIcon from "../UserIcon";
import { HiMenu } from "react-icons/hi";
import { AiFillHome, AiFillHeart } from "react-icons/ai";
import { IoMdClose } from "react-icons/io";
import Container from "../containers/Container";
import LanguageSelector from "../LanguageSelector";
import MenuCategoryCard from "../Category/MenuCategoryCard";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../context/LanguageContext";
import BasketIcon from "../ui/BasketIcon";
import { useCategories } from "../../services/queries";
import { Bell, MapPin, Search, Store } from "lucide-react";
import Address from "../maps/Address";

interface NavbarProps {
  isProfilePage?: boolean;
  isMobile?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
  isProfilePage = false,
  isMobile = false,
}) => {
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const { data: categories = [] } = useCategories();
  const { t } = useTranslation();
  const lang = useLanguage();
  const location = useLocation();
  const categoryRef = useRef<HTMLDivElement>(null);

  // Only hide mobile menu for product detail pages
  const hideMobileMenu = location.pathname.match(/^\/[a-z]{2}\/product\/\d+$/);

  const mobileTabClass = (isActive: boolean) =>
    isActive ? "text-white" : "text-white/60 hover:text-white/90";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoryRef.current &&
        !categoryRef.current.contains(event.target as Node)
      ) {
        setIsCategoryOpen(false);
      }
    };

    if (isCategoryOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCategoryOpen]);

  return (
    <>
      {/* Top section (hidden on Profile page for mobile, visible for desktop) */}
      {(!isProfilePage || !isMobile) && (
        <div className="flex flex-col w-full sticky top-0 z-50 bg-white">
          
          {/* Mobile Top Header: full-width dark blue gradient */}
          {isMobile && !hideMobileMenu && (
            <div className="w-full bg-brand-gradient text-white px-4 py-3.5 flex flex-col gap-2.5 shadow-md z-40 relative">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-1 text-white">
                  <MapPin className="w-5 h-5 text-white shrink-0" />
                  <Address classN="flex justify-start !m-0 text-white" />
                </div>
                <div className="flex items-center gap-1.5 text-white">
                  {/* Search Toggle Icon */}
                  <button 
                    onClick={() => setIsMobileSearchOpen(!isMobileSearchOpen)}
                    className={`p-1.5 rounded-full hover:bg-white/10 text-white transition-colors ${isMobileSearchOpen ? 'bg-white/10' : ''}`}
                    aria-label="Search"
                  >
                    <Search className="w-5 h-5" />
                  </button>
                  {/* Notification Bell */}
                  <Link 
                    to={`/${lang}/profile/notifications`} 
                    className="p-1.5 rounded-full hover:bg-white/10 text-white transition-colors" 
                    aria-label={t('notifications') || 'Notifications'}
                  >
                    <Bell className="w-5 h-5" />
                  </Link>
                </div>
              </div>
              {/* Expandable Search Input Container */}
              {isMobileSearchOpen && !isProfilePage && (
                <div className="w-full transition-all duration-300 ease-in-out origin-top transform scale-y-100 opacity-100">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <SearchInput />
                    </div> 
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Desktop Header */}
          {!isMobile && (
            <div className="flex flex-col w-full">
              {/* Desktop top bar */}
              <div className="bg-gradient-to-r w-full">
                <Container className="flex items-center justify-between gap-4">
                  <Logo width={230} />
                  <div className="flex w-full justify-between pl-10 items-center gap-4">
                    <div className="flex-1">
                      <SearchInput />
                    </div> 
                    <div className="hidden md:flex items-center gap-4">
                      <BasketIcon lang={lang} variant="second" size={24} />
                      <UserIcon />
                      <LanguageSelector />
                    </div>
                  </div>
                </Container>
              </div>

              {/* Desktop black/blue bar */}
              <nav className="w-full bg-brand-gradient text-white" aria-label="Main navigation">
                <Container className="hidden md:flex h-[60px] justify-between items-center gap-12">
                  <div className="flex items-center pt-2 gap-4">
                    <button
                      className="flex flex-col justify-center items-center cursor-pointer text-white hover:text-amber-500"
                      onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                      aria-label={t("category")}
                    >
                      {isCategoryOpen ? (
                        <IoMdClose size={24} />
                      ) : (
                        <HiMenu size={24} />
                      )}
                      <p>{t("category")}</p>
                    </button>
                    <Link
                      to={`/${lang}`}
                      className={`flex flex-col justify-center items-center cursor-pointer transition-colors hover:text-amber-500 ${
                        location.pathname === `/${lang}` ? "text-amber-500" : ""
                      }`}
                      aria-label={t("home")}
                    >
                      <AiFillHome size={24} />
                      <p>{t("home")}</p>
                    </Link>
                    <Link
                      to={`/${lang}/favorites`}
                      className={`flex flex-col justify-center items-center cursor-pointer transition-colors hover:text-amber-500 ${
                        location.pathname.startsWith(`/${lang}/favorites`) ? "text-amber-500" : ""
                      }`}
                      aria-label={t("favorites")}
                    >
                      <AiFillHeart size={24} />
                      <p>{t("favorites")}</p>
                    </Link>
                  </div>
                  <Address />
                </Container>
              </nav>
            </div>
          )}

        </div>
      )}

      {/* Mobile bottom bar */}
      {isMobile && (
        <nav className="fixed bottom-0 left-0 right-0 h-[60px] bg-brand-gradient text-white flex justify-around items-center z-50" aria-label="Main navigation">
          <Link
            to={`/${lang}`}
            className={`flex flex-col items-center justify-center transition-colors ${mobileTabClass(location.pathname === `/${lang}`)}`}
            aria-label={t("home")}
          >
            <AiFillHome size={24} />
            <p className="text-xs">{t("home")}</p>
          </Link>
          <Link
            to={`/${lang}/mobile-category`}
            className={`flex flex-col items-center justify-center transition-colors ${mobileTabClass(location.pathname.startsWith(`/${lang}/mobile-category`))}`}
            aria-label={t("category")}
          >
            <HiMenu size={24} />
            <p className="text-xs">{t("category")}</p>
          </Link>
          <Link
            to={`/${lang}/basket`}
            className={`flex flex-col items-center justify-center transition-colors ${mobileTabClass(location.pathname.startsWith(`/${lang}/basket`))}`}
            aria-label={t("basket_menu")}
          >
            <BasketIcon lang={lang} variant="first" size={24} noLink={true} />
            <p className="text-xs">{t("basket_menu")}</p>
          </Link>
          <Link
            to={`/${lang}/favorites`}
            className={`flex flex-col items-center justify-center transition-colors ${mobileTabClass(location.pathname.startsWith(`/${lang}/favorites`))}`}
            aria-label={t("favorites")}
          >
            <AiFillHeart size={24} />
            <p className="text-xs">{t("favorites")}</p>
          </Link>
          <UserIcon isMobile={true} />
        </nav>
      )}

      {/* Categories dropdown */}
      {isCategoryOpen && (!isProfilePage || !isMobile) && (
        <div
          onClick={() => setIsCategoryOpen(false)}
          className="fixed z-50 bg-black/20 backdrop-blur-sm top-[120px] left-0 w-full h-screen md:block hidden"
        >
          <div
            ref={categoryRef}
            className="flex flex-wrap items-center justify-start gap-2 mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8"
          >
            {categories.map((category) => (
              <MenuCategoryCard
                key={category.id}
                title={category.name}
                image={category.image}
                id={category.id}
                onClose={() => setIsCategoryOpen(false)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default memo(Navbar);